/* ========================================================================
 * studentDemoMode — DEV-ONLY demo-data voor de testleerling
 * ------------------------------------------------------------------------
 * De testleerling (/test-student-login) heeft geen Supabase-auth en geen
 * backend-sessie. Alle echte fetches falen dan (401/CORS/400) en het hele
 * portaal blijft leeg. Deze module levert realistische nepdata zodat je
 * puur aan de frontend kunt werken.
 *
 * Het scenario is bewust ZWAAR: een leerling met 14 vakken, tientallen
 * cijfers, herkansingen, wachtende beoordelingen, oefeningen, meerdere
 * coachgesprekken en een vol rooster. Zo zie je meteen hoe elke pagina
 * zich houdt onder veel data. Alles is deterministisch (geen random) en
 * relatief aan vandaag, dus de demo veroudert nooit.
 *
 * Werking: loginAsTestStudent() zet de vlag aan; services checken
 * isStudentDemo() bovenaan en geven dan demo-data terug. Buiten
 * NODE_ENV=development kan de vlag nooit aan — productie raakt dit nooit.
 * ====================================================================== */

import { plusDagen, dagenTussen } from '../utils/kalenderWeek';

const IS_DEV = process.env.NODE_ENV === 'development';

// sessionStorage i.p.v. modulestate: overleeft hot-reload tijdens ontwikkelen
// en verdwijnt vanzelf bij het sluiten van de tab.
const FLAG = 'tw_student_demo';

export function activateStudentDemo() {
  if (IS_DEV) sessionStorage.setItem(FLAG, '1');
}

export function deactivateStudentDemo() {
  sessionStorage.removeItem(FLAG);
}

export function isStudentDemo() {
  return IS_DEV && sessionStorage.getItem(FLAG) === '1';
}

// ------------------------------------------------------------------
// Tijd-helpers: alles relatief aan "nu" zodat de demo nooit veroudert
// ------------------------------------------------------------------

function overDagen(dagen, uur = 9, minuut = 0) {
  const d = new Date();
  d.setDate(d.getDate() + dagen);
  d.setHours(uur, minuut, 0, 0);
  return d.toISOString();
}

// ------------------------------------------------------------------
// Het profiel: 14 vakken met elk een eigen niveau (Ø-cijfer waar de
// gegenereerde cijfers omheen bewegen). Bewust gemengd: uitblinkers,
// middenmoot en twee zorgenkinderen.
// ------------------------------------------------------------------

const VAKKEN = [
  { naam: 'Nederlands',       niveau: 7.4, docent: 'M. Jansen' },
  { naam: 'Engels',           niveau: 8.2, docent: 'K. de Vries' },
  { naam: 'Wiskunde',         niveau: 4.8, docent: 'P. Bakker' },
  { naam: 'Geschiedenis',     niveau: 6.9, docent: 'S. Mulder' },
  { naam: 'Aardrijkskunde',   niveau: 6.2, docent: 'T. Visser' },
  { naam: 'Biologie',         niveau: 7.8, docent: 'A. Smit' },
  { naam: 'Natuurkunde',      niveau: 5.1, docent: 'R. de Groot' },
  { naam: 'Scheikunde',       niveau: 5.9, docent: 'R. de Groot' },
  { naam: 'Economie',         niveau: 6.6, docent: 'D. Peters' },
  { naam: 'Frans',            niveau: 4.2, docent: 'C. Dubois' },
  { naam: 'Duits',            niveau: 6.0, docent: 'H. Wagner' },
  { naam: 'Maatschappijleer', niveau: 8.6, docent: 'S. Mulder' },
  { naam: 'Informatica',      niveau: 9.1, docent: 'B. van Dam' },
  { naam: 'Kunst & Cultuur',  niveau: 7.0, docent: 'L. Vermeer' },
];

const ONDERWERPEN = [
  'Hoofdstuk 1', 'Hoofdstuk 2', 'Hoofdstuk 3', 'Hoofdstuk 4', 'Herhaling H1-H2',
];

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

// Deterministische "ruis" rond het vak-niveau, met lichte stijgende lijn
function demoCijfer(vakIdx, toetsIdx) {
  const ruis = (((vakIdx * 7 + toetsIdx * 13) % 5) - 2) * 0.4; // -0.8 .. +0.8
  const trend = toetsIdx * 0.15;                                // langzaam omhoog
  // Engels (index 1) toetst bewust lager dan het oefen-niveau: showcase voor
  // de kloof-analyse "oefenen gaat goed, toetsen blijven achter"
  const toetsKloof = vakIdx === 1 ? -2.8 : 0;
  return clamp(Math.round((VAKKEN[vakIdx].niveau + ruis + trend + toetsKloof) * 10) / 10, 1, 10);
}

function mcVragen(prefix, aantal, punten = 2) {
  return Array.from({ length: aantal }, (_, i) => ({
    id: `${prefix}-q${i + 1}`,
    type: 'mc',
    points: punten,
    correctAnswer: 0,
    text: `Demo-vraag ${i + 1} over de stof`,
    options: ['Antwoord A', 'Antwoord B', 'Antwoord C', 'Antwoord D'],
  }));
}

// ------------------------------------------------------------------
// Gegenereerde toetsen + sessies (één keer opgebouwd per module-load)
// ------------------------------------------------------------------

const TOETSEN_VOL = {};   // id -> volledige toets (getTestById-shape)
const SESSIES = [];       // getSessionsByStudent-shape

function maakToets(id, vakIdx, titel, opts = {}) {
  TOETSEN_VOL[id] = {
    id,
    title: titel,
    subject: VAKKEN[vakIdx].naam,
    weight: opts.weging || 1,
    hoofdstukken: opts.hoofdstukken || [],
    questions: mcVragen(id, opts.vragen || 10, 2),
  };
  return TOETSEN_VOL[id];
}

// Deterministische antwoorden bij een pct: de eerste N vragen goed
// (answer 0 = correctAnswer), de rest fout of onbeantwoord. Zo werkt de
// vragen-inkijk op de Voortgang-pagina ook echt in de demo.
function maakAntwoorden(toets, pct) {
  const vragen = toets.questions || [];
  const goed = Math.round((pct / 100) * vragen.length);
  return vragen.map((q, i) => ({
    id: `${q.id}-antwoord`,
    questionId: q.id,
    answer: i < goed ? 0 : i % 3 === 2 ? null : 1 + (i % 3),
    shuffledIndices: null,
  }));
}

// 1. Historie: per vak 2-5 vrijgegeven toetscijfers, verspreid over ~10 weken
VAKKEN.forEach((vak, vi) => {
  const aantal = 2 + (vi % 4); // 2..5 toetsen per vak
  for (let j = 0; j < aantal; j++) {
    const toetsId = `demo-toets-${vi}-${j}`;
    const onderwerp = ONDERWERPEN[j % ONDERWERPEN.length];
    const toets = maakToets(toetsId, vi, `${vak.naam}: ${onderwerp}`, {
      weging: j % 2 === 0 ? 1 : 2,
      vragen: 10,
      hoofdstukken: onderwerp.startsWith('Hoofdstuk') ? [onderwerp] : ['Hoofdstuk 1', 'Hoofdstuk 2'],
    });
    const cijfer = demoCijfer(vi, j);
    const max = toets.questions.length * 2; // 20 punten
    const punten = Math.round(((cijfer - 1) / 9) * max * 2) / 2;
    const dagenTerug = 4 + (aantal - 1 - j) * 9 + (vi % 5); // recentste ~1 week terug
    SESSIES.push({
      id: `demo-sessie-${vi}-${j}`,
      testId: toetsId,
      testType: 'toets',
      status: 'submitted',
      startTime: overDagen(-dagenTerug, 9 + (vi % 4), 0),
      endTime: overDagen(-dagenTerug, 9 + (vi % 4), 45),
      teacherApproved: true,
      vrijgegeven: true, // zoals mijn_sessies het meegeeft (migratie 108)
      totalScore: punten,
      inzageCode: null, inzageEnabled: false, inzageOpen: false,
      answers: [], openQuestionGrades: [],
    });
  }
});

// 2. Drie recente toetsen die nog op nakijken wachten (vak 0, 4, 8)
[0, 4, 8].forEach((vi, k) => {
  const toetsId = `demo-toets-wacht-${vi}`;
  maakToets(toetsId, vi, `${VAKKEN[vi].naam}: proefwerk week ${k + 1}`, { vragen: 12 });
  SESSIES.push({
    id: `demo-sessie-wacht-${vi}`,
    testId: toetsId,
    testType: 'toets',
    status: 'submitted',
    startTime: overDagen(-(1 + k), 13, 0),
    endTime: overDagen(-(1 + k), 13, 50),
    teacherApproved: false,
    vrijgegeven: false,
    totalScore: null,
    inzageCode: null, inzageEnabled: false, inzageOpen: false,
    answers: [], openQuestionGrades: [],
  });
});

// 3. Eén open inzage bij het zorgenvak Wiskunde (laag cijfer, terugkijken)
{
  const toetsId = 'demo-toets-inzage';
  maakToets(toetsId, 2, 'Wiskunde: kwadratische vergelijkingen', { vragen: 10, weging: 2 });
  SESSIES.push({
    id: 'demo-sessie-inzage',
    testId: toetsId,
    testType: 'toets',
    status: 'submitted',
    startTime: overDagen(-6, 10, 0),
    endTime: overDagen(-6, 10, 45),
    teacherApproved: true,
    vrijgegeven: true,
    totalScore: 7.5, // (7.5/20)*9+1 = 4.4 — onvoldoende, dus inzage nuttig
    inzageCode: null, inzageEnabled: true, inzageOpen: true,
    answers: [], openQuestionGrades: [],
  });
}

// 4. Oefeningen (communicatiesessies) — HET kroonjuweel van TestWisely.
// Leerlingen maken er 40-80 per vak per jaar; toetscijfers staan toch al in
// Somtoday/Magister, dus déze data moet uitblinken. Per vak 6-10 sessies
// over de afgelopen weken, met een duidelijke leercurve (begrip-% stijgt),
// en hoofdstuk in de titel zodat de communicatie-pagina per H groepeert.
VAKKEN.forEach((vak, vi) => {
  const aantal = 6 + (vi % 5); // 6..10 oefeningen per vak
  for (let j = 0; j < aantal; j++) {
    const toetsId = `demo-toets-oef-${vi}-${j}`;
    const hoofdstuk = 1 + Math.floor(j / 3); // ~3 sessies per hoofdstuk
    const oefToets = maakToets(toetsId, vi, `${vak.naam}: oefenen hoofdstuk ${hoofdstuk}`, {
      vragen: 10,
      hoofdstukken: [`Hoofdstuk ${hoofdstuk}`],
    });
    // Leercurve: start onder niveau, klim naar erboven, met deterministische ruis
    const start = clamp(vak.niveau * 10 - 25, 15, 70);
    const eind = clamp(vak.niveau * 10 + 8, 45, 97);
    const ruis = (((vi * 11 + j * 17) % 7) - 3) * 2; // -6 .. +6
    const pct = clamp(Math.round(start + ((eind - start) * j) / Math.max(1, aantal - 1) + ruis), 10, 98);
    const dagenTerug = 2 + (aantal - 1 - j) * 5 + (vi % 3); // elke ~5 dagen een sessie
    SESSIES.push({
      id: `demo-sessie-oef-${vi}-${j}`,
      testId: toetsId,
      testType: 'communicatiesessie',
      status: 'submitted',
      startTime: overDagen(-dagenTerug, 14 + (vi % 2), 0),
      endTime: overDagen(-dagenTerug, 14 + (vi % 2), 15),
      teacherApproved: true,
      vrijgegeven: true,
      totalScore: Math.round((pct / 100) * 20 * 2) / 2,
      inzageCode: null, inzageEnabled: false, inzageOpen: false,
      answers: maakAntwoorden(oefToets, pct), openQuestionGrades: [],
    });
  }
});

// ------------------------------------------------------------------
// Beschikbare toetsen (toetsen-pagina + dashboard-hero)
// ------------------------------------------------------------------

export function demoToetsenVoorKlassen(classIds = []) {
  const klasId = classIds[0] || 'demo-klas';
  const maak = (vi, titel, extra = {}) => {
    const id = extra.id || `demo-actief-${vi}`;
    const vol = maakToets(id, vi, titel, { vragen: extra.vragen || 10 });
    return {
      id,
      title: vol.title,
      subject: VAKKEN[vi].naam,
      testCode: `DEMO${vi}`,
      isActive: true,
      teacherId: `demo-docent-${vi}`,
      createdAt: overDagen(-(vi % 6) - 1),
      testType: extra.testType || 'toets',
      duration: extra.duur || 45,
      timeLimit: extra.duur || 45,
      secure: Boolean(extra.secure),
      questions: vol.questions,
      assignedClassIds: [klasId],
      startsAt: extra.startsAt || null,
      docent_naam: VAKKEN[vi].docent,
    };
  };

  return [
    // Nu te maken
    maak(0, 'Nederlands: Grammatica hoofdstuk 4', { duur: 45, secure: true, vragen: 12 }),
    maak(3, 'Geschiedenis: De Koude Oorlog', { duur: 30 }),
    maak(9, 'Frans: Vocabulaire unité 5', { duur: 20 }),
    maak(11, 'Maatschappijleer: Rechtsstaat', { duur: 35 }),
    // Gepland (countdown op het dashboard)
    maak(2, 'Wiskunde: herkansing kwadraten', { duur: 60, secure: true, startsAt: overDagen(1, 8, 30), vragen: 15 }),
    maak(6, 'Natuurkunde: Krachten en beweging', { duur: 45, startsAt: overDagen(3, 10, 30) }),
    // Oefeningen (communicatie-pagina)
    maak(1, 'Engels: speaking practice', { testType: 'communicatiesessie', duur: 15 }),
    maak(5, 'Biologie: begrippen oefenen', { testType: 'communicatiesessie', duur: 15 }),
    maak(10, 'Duits: Wortschatz üben', { testType: 'communicatiesessie', duur: 15 }),
  ];
}

export function demoToets(testId) {
  return TOETSEN_VOL[testId] || null;
}

export function demoSessies() {
  return SESSIES;
}

// ------------------------------------------------------------------
// Coaching — shape van getMijnAfsprakenLeerling
// ------------------------------------------------------------------

export function demoAfspraken() {
  return [
    {
      id: 'demo-afspraak-1', titel: 'Coachgesprek: aanpak Wiskunde en Frans', status: 'gepland',
      gepland_start: overDagen(1, 10, 30), gepland_einde: overDagen(1, 11, 0),
      locatie: 'Lokaal B2.14', docenten: { display_name: 'M. Jansen' },
    },
    {
      id: 'demo-afspraak-2', titel: 'Mentorgesprek planning toetsweek', status: 'gepland',
      gepland_start: overDagen(2, 14, 0), gepland_einde: overDagen(2, 14, 30),
      locatie: 'Lokaal A1.03', docenten: { display_name: 'P. Bakker' },
    },
    {
      id: 'demo-afspraak-3', titel: 'Vervolgafspraak studievaardigheden', status: 'intake_ingevuld',
      gepland_start: overDagen(8, 13, 0), gepland_einde: overDagen(8, 13, 30),
      locatie: 'Online', docenten: { display_name: 'M. Jansen' },
    },
    {
      id: 'demo-afspraak-4', titel: 'Kennismakingsgesprek', status: 'afgerond',
      gepland_start: overDagen(-14, 10, 0), gepland_einde: overDagen(-14, 10, 30),
      locatie: 'Lokaal B2.14', docenten: { display_name: 'M. Jansen' },
    },
    {
      id: 'demo-afspraak-5', titel: 'Startgesprek schooljaar', status: 'afgerond',
      gepland_start: overDagen(-30, 9, 0), gepland_einde: overDagen(-30, 9, 30),
      locatie: 'Lokaal B2.14', docenten: { display_name: 'M. Jansen' },
    },
  ];
}

// Vaste intake-vragenlijst (zelfde shape als de backend: {id, vraag})
const DEMO_STANDAARDVRAGEN = [
  { id: 'demo-vraag-1', vraag: 'Hoe gaat het op dit moment met je op school?' },
  { id: 'demo-vraag-2', vraag: 'Voor welk vak of onderwerp wil je hulp?' },
  { id: 'demo-vraag-3', vraag: 'Wat heb je zelf al geprobeerd?' },
  { id: 'demo-vraag-4', vraag: 'Wat wil je aan het einde van dit gesprek bereikt hebben?' },
];

// Ingevulde antwoorden per afspraak (alleen voor de al-ingevulde en afgeronde)
const DEMO_INTAKE_ANTWOORDEN = {
  'demo-afspraak-3': [
    { vraag_id: 'demo-vraag-1', antwoord: 'Best goed, alleen wiskunde blijft lastig.' },
    { vraag_id: 'demo-vraag-2', antwoord: 'Wiskunde, vooral kwadratische vergelijkingen.' },
    { vraag_id: 'demo-vraag-3', antwoord: 'Extra oefensessies gemaakt en de uitlegvideo gekeken.' },
    { vraag_id: 'demo-vraag-4', antwoord: 'Een plan om mijn wiskundecijfer omhoog te krijgen.' },
  ],
  'demo-afspraak-4': [
    { vraag_id: 'demo-vraag-1', antwoord: 'Prima, ik vind de start van het jaar wel spannend.' },
    { vraag_id: 'demo-vraag-2', antwoord: 'Nog niets specifieks, kennismaking.' },
    { vraag_id: 'demo-vraag-3', antwoord: '-' },
    { vraag_id: 'demo-vraag-4', antwoord: 'Weten bij wie ik terecht kan met vragen.' },
  ],
};

export function demoIntake(afspraakId) {
  return {
    success: true,
    vragen: DEMO_STANDAARDVRAGEN,
    antwoorden: DEMO_INTAKE_ANTWOORDEN[afspraakId] || [],
  };
}

export function demoTerugkijken(afspraakId) {
  const afspraak = demoAfspraken().find((a) => a.id === afspraakId) || null;
  const antwoorden = (DEMO_INTAKE_ANTWOORDEN[afspraakId] || []).map((x) => ({
    antwoord: x.antwoord,
    coaching_standaardvragen: {
      vraag: DEMO_STANDAARDVRAGEN.find((v) => v.id === x.vraag_id)?.vraag || '',
    },
  }));
  return {
    success: true,
    afspraak,
    antwoorden,
    verslag: afspraakId === 'demo-afspraak-4' ? {
      inhoud: 'Fijn kennismakingsgesprek. Emma weet wat ze wil verbeteren en durft hulp te vragen. We hebben afgesproken dat ze wekelijks oefensessies maakt voor wiskunde en Frans.',
      vervolgafspraken: 'Over twee weken kort checken hoe het oefenen loopt.',
    } : null,
  };
}

// ------------------------------------------------------------------
// Rooster — shape van GET /zermelo/rooster (appointments)
// Vol rooster: zes blokken per dag, 14 vakken roterend, één uitval.
// ------------------------------------------------------------------

const LOKALEN = ['B2.14', 'A1.03', 'C0.11', 'D0.02', 'B1.08', 'Gymzaal', 'B1.02', 'C1.05'];
const BLOKKEN = [
  [8, 30], [9, 20], [10, 30], [11, 20], [13, 0], [13, 50],
];

export function demoRooster(weekStart) {
  const base = new Date(weekStart);
  base.setHours(0, 0, 0, 0);

  function les(dagIndex, blokIndex, vakIdx, extra = {}) {
    const [uur, minuut] = BLOKKEN[blokIndex];
    const start = plusDagen(base, dagIndex);
    start.setHours(uur, minuut, 0, 0);
    const eind = new Date(start.getTime() + 50 * 60 * 1000);
    return {
      id: `demo-les-${dagIndex}-${blokIndex}`,
      start_at: start.toISOString(), end_at: eind.toISOString(),
      subjects: [VAKKEN[vakIdx].naam],
      locations: [LOKALEN[(vakIdx + blokIndex) % LOKALEN.length]],
      type: 'lesson', cancelled: false, gekoppelde_toets: null,
      ...extra,
    };
  }

  const lessen = [];
  for (let dag = 0; dag < 5; dag++) {
    for (let blok = 0; blok < BLOKKEN.length; blok++) {
      // Eén tussenuur per dag (wisselend blok) zodat het rooster niet dichtgetimmerd is
      if (blok === (dag + 2) % BLOKKEN.length) continue;
      const vakIdx = (dag * BLOKKEN.length + blok) % VAKKEN.length;
      const extra = {};
      // Dinsdag blok 2: toets gekoppeld aan de les (blok 3 is daar tussenuur)
      if (dag === 1 && blok === 2) {
        extra.gekoppelde_toets = { toets_code: 'DEMO0', titel: 'Nederlands: Grammatica hoofdstuk 4' };
      }
      // Donderdag blok 1: uitval
      if (dag === 3 && blok === 1) extra.cancelled = true;
      lessen.push(les(dag, blok, vakIdx, extra));
    }
  }

  // In het weekend valt "vandaag" buiten ma-vr; geef die dag toch twee
  // lessen zodat de Vandaag-widget in de demo nooit leeg is.
  const vandaag = new Date();
  vandaag.setHours(0, 0, 0, 0);
  const dagIndexVandaag = dagenTussen(base, vandaag);
  if (dagIndexVandaag > 4) {
    lessen.push(
      les(dagIndexVandaag, 2, 0),
      les(dagIndexVandaag, 3, 2, {
        gekoppelde_toets: { toets_code: 'DEMO2', titel: 'Wiskunde: herkansing kwadraten' },
      }),
    );
  }

  return lessen;
}
